import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

function DateInputField({
  label,
  value,
  onChange,
  required = false,
  fullWidth = true,
  disabled = false,
  minDate,
  maxDate,
  disableFuture = false,
  disablePast = false,
  error = false,
  helperText,
  format = 'MM/DD/YYYY',
  ...rest
}) {
  const textFieldStyles = {
    width: '100%',
    '& .MuiPickersOutlinedInput-root': {
      color: 'white',
      backgroundColor: 'transparent',
      '& .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.2)',
      },
      '&:hover .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: '#F6911D',
      },
      '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: '#F6911D',
      },
    },
    '& .MuiInputBase-input': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': { color: '#F6911D' },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#F6911D' },
    '& .MuiFormHelperText-root': { color: '#ef4444' },
  };

  const popperStyles = {
    '& .MuiPaper-root': {
      backgroundColor: '#1a1a1a',
      color: 'white',
    },
    '& .MuiPickersDay-root': {
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(246,145,29,0.2)' },
      '&.Mui-selected': {
        backgroundColor: '#F6911D',
        '&:hover': { backgroundColor: '#E54004' },
      },
    },
    '& .MuiPickersYear-yearButton': {
      color: 'white',
      '&:hover': { backgroundColor: 'rgba(246,145,29,0.2)' },
      '&.Mui-selected': {
        backgroundColor: '#F6911D',
        '&:hover': { backgroundColor: '#E54004' },
      },
    },
    '& .MuiPickersCalendarHeader-root': { color: 'white' },
    '& .MuiPickersArrowSwitcher-button': { color: '#F6911D' },
    '& .MuiDayCalendar-weekDayLabel': { color: 'rgba(255,255,255,0.6)' },
  };

  const dateDialogStyles = {
    '& .MuiDialog-paper': { backgroundColor: '#1a1a1a', color: 'white' },
    '& .MuiPickersToolbar-root': { backgroundColor: '#1a1a1a' },
    '& .MuiPickersToolbarText-root': { color: 'rgba(255,255,255,0.6)' },
    '& .MuiPickersToolbarText-root.Mui-selected': { color: 'white' },

    '& .MuiPickersCalendarHeader-root': { color: 'white' },
    '& .MuiPickersCalendarHeader-label': { color: 'white' },
    '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.6)' },

    '& .MuiDayCalendar-weekDayLabel': { color: 'rgba(255,255,255,0.4)' },

    // Day numbers
    '& .MuiPickersDay-root': {
      color: 'white',
      backgroundColor: 'transparent',
      '&:hover': { backgroundColor: 'rgba(246,145,29,0.15)' },
      '&.Mui-selected': {
        backgroundColor: '#F6911D',
        color: 'white',
        '&:hover': { backgroundColor: '#E54004' },
      },
      '&.MuiPickersDay-today': {
        borderColor: '#F6911D',
      },
    },

    // Year/month picker
    '& .MuiPickersYear-yearButton': {
      color: 'white',
      '&.Mui-selected': { backgroundColor: '#F6911D', color: 'white' },
      '&:hover': { backgroundColor: 'rgba(246,145,29,0.15)' },
    },
    '& .MuiPickersMonth-monthButton': {
      color: 'white',
      '&.Mui-selected': { backgroundColor: '#F6911D', color: 'white' },
      '&:hover': { backgroundColor: 'rgba(246,145,29,0.15)' },
    },

    '& .MuiDialogActions-root': { backgroundColor: '#1a1a1a' },
    '& .MuiButton-root': { color: '#F6911D' },
  };

  const dayjsValue = value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null;

  const handleChange = (newValue) => {
    if (onChange) onChange(newValue ? newValue.format('YYYY-MM-DD') : null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={dayjsValue}
        onChange={handleChange}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        disableFuture={disableFuture}
        disablePast={disablePast}
        format={format}
        slotProps={{
          textField: {
            fullWidth,
            required,
            error,
            helperText,
            sx: textFieldStyles,
          },
          popper: { sx: popperStyles },
          dialog: { sx: dateDialogStyles },
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
}

export default DateInputField;
